import React, { useContext } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/authContext";
import { Box, HeadingStyled, TextStyled } from "../styled/Elements";

const EnvelopeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="#0d9488" strokeWidth="1.8" />
    <path
      d="M4 7.5L12 13L20 7.5"
      stroke="#0d9488"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Home / search CTA for the broadcast general-request flow.
 * variant="inline" sits beside the expert search bar; default is a full-width card.
 */
const GeneralRequestCta = ({ overlapBanner = false, variant = "default" }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const isRtl = i18n.dir() === "rtl";
  const isInline = variant === "inline";

  const openForm = () => {
    if (isAuthenticated) {
      navigate("/general-request");
      return;
    }
    navigate("/login?next=/general-request");
  };

  return (
    <CtaWrap $overlapBanner={overlapBanner} $inline={isInline}>
      <button type="button" className="cta-card" onClick={openForm}>
        <span className="cta-icon">
          <EnvelopeIcon />
        </span>
        <Box className="cta-copy">
          <HeadingStyled
            size={isInline ? "16px" : "18px"}
            color="#002F56"
            className="cta-title"
          >
            {t("home_general_request_title")}
          </HeadingStyled>
          <TextStyled
            size={isInline ? "13px" : "14px"}
            color="#5B6B80"
            className="cta-sub"
          >
            {t("home_general_request_subtitle")}
          </TextStyled>
        </Box>
        <span className="cta-chevron" aria-hidden="true">
          {isRtl ? "<" : ">"}
        </span>
      </button>
    </CtaWrap>
  );
};

const CtaWrap = styled.div`
  width: ${(props) => (props.$inline ? "100%" : "100%")};
  max-width: ${(props) => (props.$inline ? "320px" : "1100px")};
  flex: ${(props) => (props.$inline ? "0 1 320px" : "initial")};
  margin: ${(props) =>
    props.$inline
      ? "0"
      : props.$overlapBanner
      ? "-36px auto 28px"
      : "24px auto"};
  padding: ${(props) => (props.$inline ? "0" : "0 16px")};
  position: relative;
  z-index: 4;
  align-self: stretch;
  display: flex;

  .cta-card {
    width: 100%;
    display: flex;
    align-items: center;
    gap: ${(props) => (props.$inline ? "12px" : "16px")};
    text-align: start;
    padding: ${(props) => (props.$inline ? "12px 14px" : "16px 18px")};
    border-radius: 16px;
    border: 1px solid #c5e4e8;
    background: #ffffff;
    box-shadow: 0 10px 28px rgba(0, 47, 86, 0.1);
    cursor: pointer;
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    &:hover {
      box-shadow: 0 14px 32px rgba(0, 47, 86, 0.14);
      transform: translateY(-1px);
    }
  }
  .cta-icon {
    flex-shrink: 0;
    width: ${(props) => (props.$inline ? "44px" : "52px")};
    height: ${(props) => (props.$inline ? "44px" : "52px")};
    border-radius: 50%;
    background: #e6f3f4;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cta-copy {
    flex: 1;
    min-width: 0;
  }
  .cta-title {
    font-family: var(--semiBold);
    margin-bottom: 2px;
    body.rtl & {
      font-family: var(--arSemiBold);
    }
  }
  .cta-sub {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  /* Force LTR so Bootstrap/page RTL does not mirror the chevron glyph */
  .cta-chevron {
    flex-shrink: 0;
    width: ${(props) => (props.$inline ? "36px" : "40px")};
    height: ${(props) => (props.$inline ? "36px" : "40px")};
    border-radius: 10px;
    background: #002f56;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${(props) => (props.$inline ? "20px" : "22px")};
    font-weight: 700;
    direction: ltr;
    unicode-bidi: isolate;
  }

  @media (max-width: 900px) {
    max-width: 100%;
    flex: 1 1 auto;
  }
`;

export default GeneralRequestCta;
